import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import LoadingSpinner from "../components/atoms/LoadingSpinner";
import { ProductFamilyGroup } from "../components/molecules/ProductFamilyGroup";
import { useDataReviewVariants } from "../hooks/data-review";
import type { VariantL10n } from "../services/data-review";
import { LocaleService } from "../services/locale";
import {
  clearReviewedForLocale,
  getReviewedCount,
  getReviewedVariantIds,
} from "../utils/reviewed-products";

export const DataReviewPage: FC = () => {
  const locale = LocaleService.getSelectedLocale();
  const [searchFilter, setSearchFilter] = useState("");
  const [hideReviewed, setHideReviewed] = useState(true);
  const [reviewedCount, setReviewedCount] = useState(0);

  const { data: variants, isLoading, error } = useDataReviewVariants(locale);

  // Update reviewed count when locale changes
  useEffect(() => {
    if (locale) {
      setReviewedCount(getReviewedCount(locale));
    }
  }, [locale]);

  // Force re-render when a product is marked as reviewed
  const handleProductReviewed = useCallback(() => {
    if (locale) {
      setReviewedCount(getReviewedCount(locale));
    }
  }, [locale]);

  // Clear all reviewed products for current locale
  const handleClearReviewed = useCallback(() => {
    if (locale && confirm(`Clear all reviewed products for ${locale}?`)) {
      clearReviewedForLocale(locale);
      setReviewedCount(0);
    }
  }, [locale]);

  // Group variants by family ID and filter
  const groupedVariants = useMemo(() => {
    if (!variants) {
      return [];
    }

    let filtered = variants;

    // Filter out reviewed products if hideReviewed is true
    if (hideReviewed && locale) {
      const reviewedIds = getReviewedVariantIds(locale);
      filtered = filtered.filter(
        (variant) => !reviewedIds.includes(variant.variantId)
      );
    }

    // Apply search filter
    if (searchFilter.trim()) {
      const lowerSearch = searchFilter.toLowerCase();
      filtered = filtered.filter((variant) => {
        return (
          variant.name?.toLowerCase().includes(lowerSearch) ||
          variant.sku?.toLowerCase().includes(lowerSearch) ||
          variant.description?.toLowerCase().includes(lowerSearch) ||
          variant.benefits?.toLowerCase().includes(lowerSearch) ||
          variant.ingredients?.toLowerCase().includes(lowerSearch)
        );
      });
    }

    // Group by family ID
    const familyMap = new Map<number, VariantL10n[]>();

    filtered.forEach((variant) => {
      const familyId = variant.familyId;
      if (!familyMap.has(familyId)) {
        familyMap.set(familyId, []);
      }
      familyMap.get(familyId)!.push(variant);
    });

    // Convert to array and sort by family name
    return Array.from(familyMap.entries())
      .map(([familyId, variants]) => ({
        familyId,
        familyName: variants.at(0)?.name ?? `Family ${familyId}`,
        variants: variants.sort((a, b) => a.name.localeCompare(b.name)),
      }))
      .sort((a, b) => a.familyName.localeCompare(b.familyName));
  }, [variants, searchFilter, hideReviewed, locale, reviewedCount]);

  // Calculate total filtered variants count
  const totalFilteredCount = useMemo(() => {
    return groupedVariants.reduce(
      (sum, group) => sum + group.variants.length,
      0
    );
  }, [groupedVariants]);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            Product Import Review
          </h1>
          <p className="text-gray-600 mt-2">
            Review and edit product variant data for different locales
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search Filter */}
            <div>
              <label
                htmlFor="search"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Search Products
              </label>
              <input
                id="search"
                type="text"
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                placeholder="Search by name, SKU, description..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Results Count and Actions */}
          {variants && (
            <div className="mt-4 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Showing {totalFilteredCount} of {variants.length} products in{" "}
                {groupedVariants.length}{" "}
                {groupedVariants.length === 1 ? "family" : "families"}
                {reviewedCount > 0 && (
                  <span className="ml-2 text-green-600">
                    ({reviewedCount} reviewed)
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={hideReviewed}
                    onChange={(e) => setHideReviewed(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  Hide reviewed
                </label>
                {reviewedCount > 0 && (
                  <button
                    onClick={handleClearReviewed}
                    className="px-3 py-1 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                  >
                    Clear reviewed ({reviewedCount})
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        {isLoading && (
          <div className="flex justify-center items-center min-h-[400px]">
            <LoadingSpinner size="lg" />
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
            <p className="font-medium">Error loading variants</p>
            <p className="text-sm mt-1">
              {error instanceof Error ? error.message : "Unknown error"}
            </p>
          </div>
        )}

        {!isLoading && !error && groupedVariants && locale && (
          <div className="space-y-6">
            {groupedVariants.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                <p className="text-gray-500">
                  No products found matching your search criteria.
                </p>
              </div>
            ) : (
              groupedVariants.map((group) => (
                <ProductFamilyGroup
                  key={`family-${group.familyId}-${locale}`}
                  familyId={group.familyId}
                  familyName={group.familyName}
                  variants={group.variants}
                  locale={locale}
                  onMarkAsReviewed={handleProductReviewed}
                />
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};
